import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Input from '@mui/material/Input';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { TeamOfTeams, TableTeamOfTeamsRow } from './TableTeamOfTeamsRow';
import { EnhancedTableHead } from './TableUtils';
import TeamOfTeamsAnalysis from './TeamOfTeamsAnalysis';

interface TableTeamofTeamsProps {
  rows: TeamOfTeams[];
}

export const TableTeamOfTeams: React.FC<TableTeamofTeamsProps> = ({ rows }) => {
  const { t } = useTranslation();
  const [page, setPage] = React.useState<number>(0);
  const [titleFilter, setTitleFilter] = React.useState<string>('');
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(25);
  const [analysisModalOpen, setAnalysisModalOpen] = React.useState<TeamOfTeams | null>(null);
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...rows]
        .filter((s) => s.id?.toLowerCase().includes(titleFilter))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, rows, titleFilter]
  );

  const jsonAnalysis = React.useMemo(
    () => (analysisModalOpen?.analysis ? analysisModalOpen.analysis : null),
    [analysisModalOpen]
  );

  return rows.length > 0 ? (
    <Box>
      <Dialog
        fullScreen={true}
        maxWidth={false}
        open={analysisModalOpen !== null}
        onClose={() => setAnalysisModalOpen(null)}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>{analysisModalOpen && analysisModalOpen.id}</DialogTitle>
        {analysisModalOpen && jsonAnalysis && (
          <DialogContent>
            <Box paddingX={4} style={{ textAlign: 'left' }}>
              <TeamOfTeamsAnalysis key={analysisModalOpen.id} data={jsonAnalysis} />
            </Box>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setAnalysisModalOpen(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
      <Toolbar
        sx={[
          {
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
          },
        ]}
      >
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h5"
          id="tableTitle"
          component="div"
          textAlign={'initial'}
        >
          {t('team_of_teams.title')}
        </Typography>
        <Input
          id="titleFilter"
          value={titleFilter}
          placeholder={t('common.filter_by_title')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitleFilter(e.target.value)}
        />
      </Toolbar>
      <TableContainer>
        <Table aria-labelledby="tableTitle" size={'small'}>
          <EnhancedTableHead
            order={'asc'}
            orderBy={'id'}
            onRequestSort={() => console.log('no sorting')}
          />
          <TableBody>
            {visibleRows.map((row, index) => {
              return (
                <TableTeamOfTeamsRow
                  key={index}
                  row={row}
                  setAnalysisModalOpen={setAnalysisModalOpen}
                />
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 33 * emptyRows,
                }}
              >
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  ) : (
    <Typography>{t('team_of_teams.no_analysis')}</Typography>
  );
};
